"use client";

/**
 * GoogleAnalytics Component
 * 
 * Conditionally loads Google Analytics based on user's cookie consent.
 * Only loads when analytics consent has been given.
 * 
 * Usage: Add to your public layout
 * Required: Set NEXT_PUBLIC_GA_MEASUREMENT_ID in your environment variables
 */

import { useEffect, useState } from "react";
import Script from "next/script";
import { hasAnalyticsConsent, getStoredConsent } from "@/lib/cookieConsent";

// Get GA Measurement ID from environment variable
const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

interface WindowWithDataLayer extends Window {
  dataLayer: unknown[];
  gtag: (...args: unknown[]) => void;
}

declare const window: WindowWithDataLayer;

/**
 * Initialize gtag with default consent settings (denied by default)
 */
function initializeGtagConsent() {
  if (typeof window === "undefined") return;
  
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  
  // Set default consent to denied
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
}

/**
 * Update gtag consent based on user preferences
 */
function updateGtagConsent(analyticsAllowed: boolean, marketingAllowed: boolean) {
  if (typeof window === "undefined" || !window.gtag) return;
  
  window.gtag("consent", "update", {
    analytics_storage: analyticsAllowed ? "granted" : "denied",
    ad_storage: marketingAllowed ? "granted" : "denied",
    ad_user_data: marketingAllowed ? "granted" : "denied",
    ad_personalization: marketingAllowed ? "granted" : "denied",
  });
}

export function GoogleAnalytics() {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize gtag with denied consent by default
    initializeGtagConsent();
    setIsInitialized(true);

    // Check initial consent status
    const consent = getStoredConsent();
    if (consent?.analytics) {
      setShouldLoad(true);
      updateGtagConsent(consent.analytics, consent.marketing);
    }

    // Listen for consent changes
    const handleConsentChange = (event: CustomEvent) => {
      const newConsent = event.detail;
      if (newConsent?.analytics) {
        setShouldLoad(true);
        updateGtagConsent(newConsent.analytics, newConsent.marketing);
      } else {
        // If consent revoked, update gtag to denied
        updateGtagConsent(false, false);
      }
    };

    window.addEventListener(
      "cookieConsentUpdated", 
      handleConsentChange as EventListener
    );

    return () => {
      window.removeEventListener(
        "cookieConsentUpdated", 
        handleConsentChange as EventListener
      );
    };
  }, []);

  // Don't render anything if no GA ID is configured
  if (!GA_MEASUREMENT_ID) {
    return null;
  }

  // Only load scripts if consent given and initialized
  if (!shouldLoad || !isInitialized) {
    return null;
  }

  return (
    <>
      {/* Google Analytics Script */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script
        id="google-analytics"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${GA_MEASUREMENT_ID}', {
              page_path: window.location.pathname,
            });
          `,
        }}
      />
    </>
  );
}

/**
 * Hook to track page views - call this in your layout or page components
 */
export function usePageView(path: string) {
  useEffect(() => {
    if (typeof window === "undefined" || !window.gtag || !hasAnalyticsConsent()) {
      return;
    }

    window.gtag("config", GA_MEASUREMENT_ID, {
      page_path: path,
    });
  }, [path]);
}

/**
 * Track a custom event
 */
export function trackEvent(
  action: string,
  category: string,
  label?: string,
  value?: number
) {
  if (typeof window === "undefined" || !window.gtag || !hasAnalyticsConsent()) {
    return;
  }

  window.gtag("event", action, {
    event_category: category,
    event_label: label,
    value: value,
  });
}
